import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Home, ArrowLeft, Search, Shield, HeartHandshake, FileText } from 'lucide-react';
import BrandLogo from '@/components/brand/BrandLogo';
import { useAuth } from '@/contexts/AuthContext';

export default function NotFoundPage() {
  const { isAuthenticated } = useAuth();

  return (
    <div className="min-h-screen bg-cream-paper flex flex-col justify-between text-forest-ink selection:bg-keylime selection:text-forest-ink">
      <Helmet>
        <title>Page Not Found | ByOnco</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      {/* Top minimal header */}
      <header className="w-full px-6 py-5 max-w-6xl mx-auto flex items-center justify-between">
        <Link to="/" className="inline-flex items-center gap-2">
          <BrandLogo size="md" />
        </Link>
        <Link
          to="/"
          className="text-sm font-medium text-forest-ink hover:text-forest-shadow flex items-center gap-1.5 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to ByOnco
        </Link>
      </header>

      {/* Main content */}
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="max-w-xl w-full text-center space-y-8">
          <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-mist border border-hair text-xs font-semibold tracking-wider uppercase text-forest-ink">
            Error 404 · Page Not Found
          </div>

          <div className="space-y-3">
            <h1 className="text-3xl sm:text-5xl font-light font-serif tracking-tight text-forest-ink">
              We couldn’t find that page
            </h1>
            <p className="text-charcoal/80 text-base sm:text-lg max-w-md mx-auto leading-relaxed">
              The link you followed may have changed, or the address might contain a typo. Let’s get you back on track.
            </p>
          </div>

          {/* Quick primary actions */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
            <Link
              to={isAuthenticated ? '/app' : '/'}
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-forest-ink text-white font-medium text-sm hover:bg-forest-shadow transition-colors shadow-sm"
            >
              <Home className="w-4 h-4" />
              {isAuthenticated ? 'Go to Companion App' : 'Return to Homepage'}
            </Link>

            <Link
              to="/find-cancer-centers"
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-white border border-border-mist text-forest-ink font-medium text-sm hover:bg-mist transition-colors shadow-2xs"
            >
              <Search className="w-4 h-4" />
              Find Cancer Centers
            </Link>
          </div>

          {/* Helpful directory links */}
          <div className="pt-8 border-t border-border-mist/60 grid grid-cols-1 sm:grid-cols-3 gap-3 text-left">
            <Link
              to="/cancer"
              className="p-3.5 rounded-2xl bg-white/70 border border-border-mist/70 hover:border-forest-ink/40 hover:bg-white transition-all group"
            >
              <div className="text-xs font-semibold text-forest-ink flex items-center gap-1.5 mb-1">
                <FileText className="w-3.5 h-3.5 text-forest-ink group-hover:translate-x-0.5 transition-transform" />
                Cancer Guides
              </div>
              <p className="text-xs text-charcoal/70">
                Evidence-based summaries and clinical protocols.
              </p>
            </Link>

            <Link
              to="/second-opinion"
              className="p-3.5 rounded-2xl bg-white/70 border border-border-mist/70 hover:border-forest-ink/40 hover:bg-white transition-all group"
            >
              <div className="text-xs font-semibold text-forest-ink flex items-center gap-1.5 mb-1">
                <Shield className="w-3.5 h-3.5 text-forest-ink group-hover:translate-x-0.5 transition-transform" />
                Second Opinion
              </div>
              <p className="text-xs text-charcoal/70">
                Independent oncologist chart reviews and guidance.
              </p>
            </Link>

            <Link
              to="/subscribe"
              className="p-3.5 rounded-2xl bg-white/70 border border-border-mist/70 hover:border-forest-ink/40 hover:bg-white transition-all group"
            >
              <div className="text-xs font-semibold text-forest-ink flex items-center gap-1.5 mb-1">
                <HeartHandshake className="w-3.5 h-3.5 text-forest-ink group-hover:translate-x-0.5 transition-transform" />
                ByOnco PRO
              </div>
              <p className="text-xs text-charcoal/70">
                Membership plans, 24/7 AI companion, and voice.
              </p>
            </Link>
          </div>
        </div>
      </main>

      {/* Footer support note */}
      <footer className="w-full px-6 py-6 max-w-6xl mx-auto text-center text-xs text-charcoal/60">
        If you think this is a technical issue or need urgent clinical support, reach our team at{' '}
        <a href="mailto:care@getbyonco.com" className="underline text-forest-ink font-medium">
          care@getbyonco.com
        </a>.
      </footer>
    </div>
  );
}
